import React, { useState, useEffect, useCallback } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/de';

import { 
    Dialog, DialogTitle, DialogContent, DialogActions, 
    Button, Box, Typography, Stack, IconButton, Divider, Paper
} from '@mui/material';
import { GiTennisCourt } from "react-icons/gi";
import { MdClose, MdDelete } from "react-icons/md";
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from "../provider/auth";
import { API_BASE_URL } from '../App.js';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("Europe/Berlin");

function ShowBookingModal({ show, onHide, booking, players }) {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [playerName, setPlayerName] = useState("");

    const getNameById = useCallback((id, fallbackName) => {
        if (!id) return fallbackName || "Unbekannt";
        if (typeof id === 'object' && id.name) return id.name.trim();
        if (typeof id === 'object' && (id.firstName || id.lastName)) return `${id.firstName || ''} ${id.lastName || ''}`.trim();
        
        const found = players?.find(item => String(item.id) === String(id));
        return found ? found.name.trim() : (fallbackName || "Unbekannt").trim();
    }, [players]);

    useEffect(() => {
        if (booking) {
            setPlayerName(getNameById(booking.playerId, booking.playerSnapshot));
        }
    }, [booking, getNameById]);

    const handleDelete = async (id) => {
        setLoading(true);
        try {
            await axios.delete(`${API_BASE_URL}/api/bookings/${id}`);
            toast.success("Buchung erfolgreich gelöscht");
            onHide(true);
        } catch (error) {
            toast.error(error.response?.data?.message || "Fehler beim Löschen");
        } finally {
            setLoading(false);
        }
    };

    if (!booking) return null;

    const notStartedYet = dayjs(booking.startTime).isAfter(dayjs());

    return (
        <Dialog open={show} onClose={() => onHide()} fullWidth maxWidth="xs">
            <DialogTitle sx={{ 
                bgcolor: 'primary.main', color: 'white', px: 3, py: 2,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <GiTennisCourt size={28} />
                    <Typography variant="h6" fontWeight="bold">Platz {booking.courtId}</Typography>
                </Box>
                <IconButton onClick={() => onHide()} sx={{ color: 'white' }} size="small">
                    <MdClose />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ px: 3, pt: 1 }}>
                <Stack spacing={4} sx={{ mt: 3 }}>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                        <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 'bold', textTransform: 'uppercase', textAlign: 'center' }}>
                            Buchungsdetails
                        </Typography>
                        
                        <Stack spacing={1.5}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" color="text.secondary">Datum:</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                    {dayjs(booking.startTime).tz().format("DD.MM.YYYY")}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" color="text.secondary">Zeitraum:</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                    {dayjs(booking.startTime).tz().format("HH:mm")} - {dayjs(booking.endTime).tz().format("HH:mm")} Uhr
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" color="text.secondary">Modus:</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                    {booking.gameType === "single" ? "Einzel" : "Doppel"}
                                </Typography>
                            </Box>

                            {(() => {
                                const guestEmail = (process.env.REACT_APP_GUEST_EMAIL || 'gast@tc-opfingen.de').toLowerCase();
                                const guestPlayer = players?.find(p => p.email?.toLowerCase() === guestEmail);
                                const guestId = guestPlayer?.id;
                                const isOwner = String(booking.playerId) === String(user?.playerId) || String(booking.mainPlayer) === String(user?.playerId);
                                const isAdmin = user?.role >= 1;

                                if ((isOwner || isAdmin) && guestId && booking.partners?.some(p => String(p) === String(guestId))) {
                                    const guestCount = booking.partners.filter(p => String(p) === String(guestId)).length;
                                    const duration = dayjs(booking.endTime).diff(dayjs(booking.startTime), 'minute') / 60;
                                    const rate = booking.gameType === 'single' ? 5 : 2.5;
                                    const price = (guestCount * duration * rate).toFixed(2).replace('.', ',');

                                    return (
                                        <>
                                            <Divider sx={{ my: 0.5 }} />
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2" color="error" sx={{ fontWeight: 'medium' }}>Gastgebühren:</Typography>
                                                <Typography variant="body2" color="error" fontWeight="bold">{price} €</Typography>
                                            </Box>
                                        </>
                                    );
                                }
                                return null;
                            })()}

                            <Box sx={{ mt: 1, textAlign: 'left' }}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 'bold', textTransform: 'uppercase' }}>
                                    Spieler
                                </Typography>
                                <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'white', borderRadius: 2 }}>
                                    <Stack spacing={0.5}>
                                        <Typography variant="body2" fontWeight="500">
                                            {playerName}
                                        </Typography>
                                        {booking.partners?.map((p, idx) => (
                                            <Typography key={idx} variant="body2">
                                                {getNameById(p)}
                                            </Typography>
                                        ))}
                                    </Stack>
                                </Paper>
                            </Box>
                        </Stack>
                    </Paper>
                </Stack>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2.5, bgcolor: '#f8f9fa' }}>
                <Box sx={{ width: '100%', display: 'flex', gap: 1.5, justifyContent: 'center' }}>
                    {booking && user && notStartedYet && (
                        (String(booking.playerId) === String(user.playerId) || 
                         String(booking.mainPlayer) === String(user.playerId) || 
                         booking.partners?.some(p => String(p) === String(user.playerId)) || 
                         user.role >= 1) && (
                            <Button 
                                variant="outlined" 
                                color="error" 
                                onClick={() => handleDelete(booking._id)} 
                                disabled={loading}
                                startIcon={<MdDelete />}
                                fullWidth
                            >
                                {loading ? "Wird gelöscht..." : "Buchung löschen"}
                            </Button>
                        )
                    )}
                    <Button onClick={() => onHide()} variant="text" color="inherit" fullWidth>
                        Schließen
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
}

export default ShowBookingModal;
